import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Segmented, Statistic, Tooltip, Typography } from 'antd'
import { HomeOutlined, LoadingOutlined, RedoOutlined, UndoOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons'
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { AxisPointerComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { IMUAnalysis, IMUBucket, IMUResponse, TimestampNS, Vector3Envelope, Vector3Stats } from '../types'
import { api } from '../api'
import { relativeSeconds } from '../lib/time'
import {
  appendZoomHistory,
  createZoomHistory,
  isFullZoomRange,
  navigateZoomHistory,
  normalizeZoomRange,
  sameZoomRange,
  zoomOutRange,
  type ZoomRange,
} from '../lib/chartZoom'

echarts.use([LineChart, AxisPointerComponent, DataZoomComponent, GridComponent, LegendComponent, TooltipComponent, CanvasRenderer])

type Mode = 'accel' | 'gyro' | 'rpy'
type DetailWindow = { key: string; from: TimestampNS; to: TimestampNS; response: IMUResponse }
type BoxSelection = { left: number; top: number; width: number; height: number }

const colors = ['#58a6ff', '#f4b860', '#52d6ad']
const lineTypes = ['solid', 'dashed', 'dotted'] as const
const fullRange: ZoomRange = { start: 0, end: 100 }
const modes: Record<Mode, { label: string; axes: string[]; unit: string; scale: number }> = {
  accel: { label: '加速度', axes: ['Ax', 'Ay', 'Az'], unit: 'm/s²', scale: 1 },
  gyro: { label: '角速度', axes: ['Gx', 'Gy', 'Gz'], unit: 'rad/s', scale: 1 },
  rpy: { label: '姿态角', axes: ['Roll', 'Pitch', 'Yaw'], unit: '°', scale: 180 / Math.PI },
}

function envelope(buckets: IMUBucket[], mode: Mode, axis: number, startNS: TimestampNS, scale: number): [number, number][] {
  const points: [number, number][] = []
  for (const bucket of buckets) {
    const values: Vector3Envelope = bucket[mode]
    const start = relativeSeconds(startNS, bucket.startNs)
    const end = relativeSeconds(startNS, bucket.endNs)
    const mid = (start + end) / 2
    points.push([start, values.first[axis] * scale], [mid - 0.000001, values.min[axis] * scale], [mid + 0.000001, values.max[axis] * scale], [end, values.last[axis] * scale])
  }
  return points
}

function zoomWindow(range: ZoomRange, analysis?: IMUAnalysis): { from: TimestampNS; to: TimestampNS; key: string } | undefined {
  if (!analysis) return undefined
  const domainStart = BigInt(analysis.startNs)
  const domainEnd = BigInt(analysis.endNs)
  if (domainEnd <= domainStart) return undefined
  const normalized = normalizeZoomRange(range)
  const scale = 1_000_000n
  const denominator = 100n * scale
  const duration = domainEnd - domainStart
  const from = domainStart + duration * BigInt(Math.round(normalized.start * Number(scale))) / denominator
  let to = domainStart + duration * BigInt(Math.round(normalized.end * Number(scale))) / denominator
  if (to <= from) to = from + 1n
  const fromText = from.toString()
  const toText = to.toString()
  return { from: fromText, to: toText, key: `${fromText}:${toText}` }
}

function mergeBuckets(base: IMUBucket[], detail?: DetailWindow): IMUBucket[] {
  if (!detail) return base
  const from = BigInt(detail.from)
  const to = BigInt(detail.to)
  return [
    ...base.filter((bucket) => BigInt(bucket.endNs) < from || BigInt(bucket.startNs) > to),
    ...detail.response.buckets,
  ].sort((left, right) => BigInt(left.startNs) < BigInt(right.startNs) ? -1 : BigInt(left.startNs) > BigInt(right.startNs) ? 1 : 0)
}

export function IMUChart({
  sessionID,
  buckets,
  analysis,
  startNS,
}: {
  sessionID: string
  buckets: IMUBucket[]
  analysis?: IMUAnalysis
  startNS: TimestampNS
}) {
  const container = useRef<HTMLDivElement>(null)
  const chartRef = useRef<echarts.EChartsType | undefined>(undefined)
  const boxZoomActiveRef = useRef(false)
  const historyRef = useRef(createZoomHistory())
  const pendingRangeRef = useRef<ZoomRange | undefined>(undefined)
  const historyTimerRef = useRef<number | undefined>(undefined)
  const detailCacheRef = useRef(new Map<string, DetailWindow>())
  const [mode, setMode] = useState<Mode>('accel')
  const [boxZoomActive, setBoxZoomActive] = useState(false)
  const [boxSelection, setBoxSelection] = useState<BoxSelection>()
  const [zoomState, setZoomState] = useState({ range: fullRange, canBack: false, canForward: false })
  const [detailWindow, setDetailWindow] = useState<DetailWindow>()
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const config = modes[mode]
  const requestedWindow = useMemo(() => zoomWindow(zoomState.range, analysis), [zoomState.range, analysis])
  const activeDetail = detailWindow?.key === requestedWindow?.key ? detailWindow : undefined
  const visibleBuckets = useMemo(() => mergeBuckets(buckets, activeDetail), [buckets, activeDetail])
  const visibleAnalysis = activeDetail?.response.analysis ?? analysis

  const publishHistory = useCallback((range?: ZoomRange, pending = false) => {
    const history = historyRef.current
    const current = history.entries[history.index] ?? fullRange
    const visible = normalizeZoomRange(range ?? current)
    setZoomState({
      range: visible,
      canBack: history.index > 0 || (pending && !sameZoomRange(visible, current)),
      canForward: !pending && history.index < history.entries.length - 1,
    })
  }, [])

  const commitPendingZoom = useCallback(() => {
    if (historyTimerRef.current !== undefined) window.clearTimeout(historyTimerRef.current)
    historyTimerRef.current = undefined
    const pending = pendingRangeRef.current
    if (!pending) return
    pendingRangeRef.current = undefined
    historyRef.current = appendZoomHistory(historyRef.current, pending)
    publishHistory()
  }, [publishHistory])

  const queueUserZoom = useCallback((range: ZoomRange) => {
    const normalized = normalizeZoomRange(range)
    pendingRangeRef.current = normalized
    publishHistory(normalized, true)
    if (historyTimerRef.current !== undefined) window.clearTimeout(historyTimerRef.current)
    historyTimerRef.current = window.setTimeout(commitPendingZoom, 220)
  }, [commitPendingZoom, publishHistory])

  const dispatchZoom = useCallback((range: ZoomRange) => {
    const normalized = normalizeZoomRange(range)
    chartRef.current?.setOption({ dataZoom: [{ start: normalized.start, end: normalized.end }, { start: normalized.start, end: normalized.end }] }, { silent: true })
  }, [])

  const applyZoom = useCallback((range: ZoomRange) => {
    commitPendingZoom()
    historyRef.current = appendZoomHistory(historyRef.current, range)
    publishHistory()
    dispatchZoom(historyRef.current.entries[historyRef.current.index])
  }, [commitPendingZoom, dispatchZoom, publishHistory])

  const navigateHistory = useCallback((delta: -1 | 1) => {
    commitPendingZoom()
    historyRef.current = navigateZoomHistory(historyRef.current, delta)
    publishHistory()
    dispatchZoom(historyRef.current.entries[historyRef.current.index])
  }, [commitPendingZoom, dispatchZoom, publishHistory])

  const toggleBoxZoom = useCallback(() => {
    const next = !boxZoomActive
    boxZoomActiveRef.current = next
    chartRef.current?.getZr().setCursorStyle(next ? 'crosshair' : 'default')
    chartRef.current?.setOption({ dataZoom: [{ id: 'imu-inside', disabled: next }] })
    setBoxSelection(undefined)
    setBoxZoomActive(next)
  }, [boxZoomActive])

  const restore = useCallback(() => {
    applyZoom(fullRange)
    boxZoomActiveRef.current = false
    chartRef.current?.getZr().setCursorStyle('default')
    chartRef.current?.setOption({ dataZoom: [{ id: 'imu-inside', disabled: false }] })
    setBoxSelection(undefined)
    setBoxZoomActive(false)
  }, [applyZoom])

  useEffect(() => {
    if (historyTimerRef.current !== undefined) window.clearTimeout(historyTimerRef.current)
    historyTimerRef.current = undefined
    pendingRangeRef.current = undefined
    historyRef.current = createZoomHistory()
    detailCacheRef.current.clear()
    setDetailWindow(undefined)
    setDetailLoading(false)
    setDetailError('')
    boxZoomActiveRef.current = false
    setBoxZoomActive(false)
    setBoxSelection(undefined)
    publishHistory()
  }, [sessionID, analysis?.startNs, analysis?.endNs, publishHistory])

  useEffect(() => {
    if (!requestedWindow || isFullZoomRange(zoomState.range)) {
      setDetailWindow(undefined)
      setDetailLoading(false)
      setDetailError('')
      return
    }
    const cached = detailCacheRef.current.get(requestedWindow.key)
    if (cached) {
      setDetailWindow(cached)
      setDetailLoading(false)
      setDetailError('')
      return
    }
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      const pixels = Math.max(900, Math.min(2400, Math.round((container.current?.clientWidth ?? 900) * 2)))
      setDetailLoading(true)
      setDetailError('')
      api.imu(sessionID, requestedWindow.from, requestedWindow.to, pixels, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) return
          const detail = { ...requestedWindow, response }
          detailCacheRef.current.set(requestedWindow.key, detail)
          while (detailCacheRef.current.size > 8) detailCacheRef.current.delete(detailCacheRef.current.keys().next().value as string)
          setDetailWindow(detail)
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) setDetailError(error instanceof Error ? error.message : String(error))
        })
        .finally(() => { if (!controller.signal.aborted) setDetailLoading(false) })
    }, 280)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [requestedWindow, sessionID, zoomState.range])

  useEffect(() => {
    if (!container.current) return
    const chart = echarts.init(container.current, undefined, { renderer: 'canvas' })
    chartRef.current = chart
    const range = zoomState.range
    chart.setOption({
      animation: false,
      color: colors,
      grid: { left: 62, right: 24, top: 42, bottom: 72 },
      legend: { top: 5, textStyle: { color: '#b8c8d9' } },
      axisPointer: { link: [{ xAxisIndex: 'all' }] },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross', label: { backgroundColor: '#20384d' } },
        valueFormatter: (value: unknown) => `${Number(value).toFixed(5)} ${config.unit}`,
      },
      dataZoom: [
        {
          id: 'imu-inside',
          type: 'inside', xAxisIndex: 0, filterMode: 'filter', start: range.start, end: range.end,
          disabled: boxZoomActiveRef.current,
          zoomOnMouseWheel: true, moveOnMouseMove: true, moveOnMouseWheel: false, preventDefaultMouseMove: true,
        },
        {
          id: 'imu-slider',
          type: 'slider', xAxisIndex: 0, filterMode: 'filter', start: range.start, end: range.end,
          height: 18, bottom: 12, borderColor: '#29445b', backgroundColor: '#081522', fillerColor: 'rgb(88 166 255 / 18%)',
          handleStyle: { color: '#58a6ff', borderColor: '#9bceff' }, textStyle: { color: '#8497aa' },
        },
      ],
      xAxis: {
        type: 'value', name: '相对时间 (s)', nameGap: 28,
        axisLabel: { color: '#8497aa', formatter: (value: number) => value.toFixed(2) },
        axisLine: { lineStyle: { color: '#385066' } }, splitLine: { lineStyle: { color: '#17283b' } },
      },
      yAxis: {
        type: 'value', name: config.unit,
        axisLabel: { color: '#8497aa' }, axisLine: { show: true, lineStyle: { color: '#385066' } },
        splitLine: { lineStyle: { color: '#17283b' } }, scale: true,
      },
      series: config.axes.map((name, axis) => ({
        name, type: 'line', data: envelope(visibleBuckets, mode, axis, startNS, config.scale),
        showSymbol: false, sampling: 'lttb', progressive: 4000,
        lineStyle: { width: axis === 2 ? 1.7 : 1.25, type: lineTypes[axis] }, emphasis: { focus: 'series' },
      })),
    })
    const onDataZoom = () => {
      const option = chart.getOption() as { dataZoom?: Array<{ start?: number; end?: number }> }
      const dataZoom = option.dataZoom?.[0]
      if (dataZoom?.start !== undefined && dataZoom.end !== undefined) queueUserZoom({ start: dataZoom.start, end: dataZoom.end })
    }
    chart.on('datazoom', onDataZoom)
    type ZRenderMouseEvent = { offsetX: number; offsetY: number; event?: { preventDefault?: () => void } }
    let dragStartX: number | undefined
    let dragRect: { x: number; y: number; width: number; height: number } | undefined
    const gridRect = () => ({ x: 62, y: 42, width: Math.max(0, chart.getWidth() - 86), height: Math.max(0, chart.getHeight() - 114) })
    const clampedX = (value: number) => dragRect ? Math.max(dragRect.x, Math.min(dragRect.x + dragRect.width, value)) : value
    const onMouseDown = (event: ZRenderMouseEvent) => {
      if (!boxZoomActiveRef.current || !chart.containPixel({ gridIndex: 0 }, [event.offsetX, event.offsetY])) return
      dragRect = gridRect()
      if (!dragRect) return
      dragStartX = clampedX(event.offsetX)
      setBoxSelection({ left: dragStartX, top: dragRect.y, width: 0, height: dragRect.height })
      event.event?.preventDefault?.()
    }
    const onMouseMove = (event: ZRenderMouseEvent) => {
      if (dragStartX === undefined || !dragRect) return
      const currentX = clampedX(event.offsetX)
      setBoxSelection({ left: Math.min(dragStartX, currentX), top: dragRect.y, width: Math.abs(currentX - dragStartX), height: dragRect.height })
      event.event?.preventDefault?.()
    }
    const finishBoxZoom = (event?: ZRenderMouseEvent) => {
      if (dragStartX === undefined || !dragRect) return
      const startX = dragStartX
      const endX = clampedX(event?.offsetX ?? dragStartX)
      dragStartX = undefined
      setBoxSelection(undefined)
      if (Math.abs(endX - startX) < 8 || !analysis) return
      const firstValue = Number(chart.convertFromPixel({ xAxisIndex: 0 }, Math.min(startX, endX)))
      const lastValue = Number(chart.convertFromPixel({ xAxisIndex: 0 }, Math.max(startX, endX)))
      const domainStart = relativeSeconds(startNS, analysis.startNs)
      const domainEnd = relativeSeconds(startNS, analysis.endNs)
      const domainDuration = domainEnd - domainStart
      if (!Number.isFinite(firstValue) || !Number.isFinite(lastValue) || domainDuration <= 0) return
      applyZoom({
        start: (firstValue - domainStart) / domainDuration * 100,
        end: (lastValue - domainStart) / domainDuration * 100,
      })
    }
    const onMouseUp = (event: ZRenderMouseEvent) => finishBoxZoom(event)
    const onGlobalOut = () => finishBoxZoom()
    chart.getZr().on('mousedown', onMouseDown)
    chart.getZr().on('mousemove', onMouseMove)
    chart.getZr().on('mouseup', onMouseUp)
    chart.getZr().on('globalout', onGlobalOut)
    chart.getZr().setCursorStyle(boxZoomActiveRef.current ? 'crosshair' : 'default')
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(container.current)
    return () => {
      commitPendingZoom()
      observer.disconnect()
      chart.off('datazoom', onDataZoom)
      chart.getZr().off('mousedown', onMouseDown)
      chart.getZr().off('mousemove', onMouseMove)
      chart.getZr().off('mouseup', onMouseUp)
      chart.getZr().off('globalout', onGlobalOut)
      chart.dispose()
      if (chartRef.current === chart) chartRef.current = undefined
    }
  // zoomState is intentionally read only when a chart instance is created; ECharts owns live zoom updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleBuckets, startNS, mode, config, queueUserZoom, commitPendingZoom])

  useEffect(() => () => {
    if (historyTimerRef.current !== undefined) window.clearTimeout(historyTimerRef.current)
  }, [])

  const stats: Vector3Stats | undefined = visibleAnalysis?.[mode]
  const windowStart = requestedWindow ? relativeSeconds(startNS, requestedWindow.from) : 0
  const windowEnd = requestedWindow ? relativeSeconds(startNS, requestedWindow.to) : 0
  const detailStatus = detailLoading ? '当前窗口重采样中' : detailError ? `重采样失败：${detailError}` : activeDetail ? '当前窗口高分辨率' : '全局 envelope'

  return <div className="imu-workbench">
    <div className="imu-toolbar">
      <div className="imu-title">
        <Typography.Text strong>IMU 时域曲线</Typography.Text>
        <Typography.Text type="secondary">{config.label} · {config.unit} · X/Y/Z 三轴</Typography.Text>
      </div>
      <Segmented size="small" value={mode} options={(Object.keys(modes) as Mode[]).map(value => ({ value, label: modes[value].label }))} onChange={value => setMode(value as Mode)} />
    </div>
    <div className="imu-stat-grid">
      {config.axes.map((axis, index) => <div className="imu-stat" key={axis}>
        <Statistic title={`${axis} ${activeDetail ? '窗口' : '全程'}平均值`} value={(stats?.mean[index] ?? 0) * config.scale} precision={5} suffix={config.unit} />
        <Typography.Text type="secondary">σ {((stats?.stdDev[index] ?? 0) * config.scale).toFixed(5)} · RMS {((stats?.rms[index] ?? 0) * config.scale).toFixed(5)}</Typography.Text>
      </div>)}
    </div>
    <div className="imu-zoom-toolbar">
      <div className="imu-zoom-actions">
        <Tooltip title="拖拽一个矩形区域放大；再次点击退出框选模式"><Button size="small" type={boxZoomActive ? 'primary' : 'default'} icon={<ZoomInOutlined />} onClick={toggleBoxZoom}>框选放大</Button></Tooltip>
        <Tooltip title="回到上一个时间窗口"><Button size="small" aria-label="后退" icon={<UndoOutlined />} disabled={!zoomState.canBack} onClick={() => navigateHistory(-1)} /></Tooltip>
        <Tooltip title="前进到下一个时间窗口"><Button size="small" aria-label="前进" icon={<RedoOutlined />} disabled={!zoomState.canForward} onClick={() => navigateHistory(1)} /></Tooltip>
        <Tooltip title="以当前窗口中心向外缩小"><Button size="small" aria-label="缩小" icon={<ZoomOutOutlined />} disabled={isFullZoomRange(zoomState.range)} onClick={() => applyZoom(zoomOutRange(zoomState.range))} /></Tooltip>
        <Tooltip title="恢复完整 IMU 时间范围"><Button size="small" icon={<HomeOutlined />} disabled={isFullZoomRange(zoomState.range)} onClick={restore}>恢复</Button></Tooltip>
      </div>
      <Typography.Text type={detailError ? 'danger' : 'secondary'} className="imu-window-label">
        {detailLoading && <LoadingOutlined spin />} {windowStart.toFixed(3)}s–{windowEnd.toFixed(3)}s · Δ{Math.max(0, windowEnd - windowStart).toFixed(3)}s · {visibleAnalysis?.sampleCount.toLocaleString() ?? 0} samples · {(visibleAnalysis?.effectiveHz ?? 0).toFixed(2)} Hz · {detailStatus}
      </Typography.Text>
    </div>
    <Typography.Text type="secondary" className="imu-interaction-hint">滚轮缩放 · 拖拽平移 · 下方滑块调整范围 · 点击图例隐藏/显示轴</Typography.Text>
    <div className="imu-chart-shell">
      <div ref={container} className="imu-chart-plot" role="img" aria-label={`IMU ${config.label}时域曲线，可框选、缩放、平移和恢复`} />
      {boxSelection && <div className="imu-box-selection" style={boxSelection} />}
    </div>
  </div>
}
